import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * The signed-in employee's own day-off / business-trip / workday lists — the
 * "member" scope of the shared HR endpoints. Mirrors datacentral-client-v4
 * `modules/{Dayoffs,Business,WorkDay}/services` (`pageType="member"`). Backs the
 * Account Home tabs. Simple paged params; the caller sets the period.
 */

export interface ListParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

/* ── My day-offs ───────────────────────────────────────────────────────── */
/**
 * One leave day. The API is inconsistent: some records use `start_day_off` /
 * `day_off_option`, others `startdayoff` / `dayoff_option`. Read via `rangeDate`.
 */
export interface DayOffRangeTime {
  start_day_off?: string;
  startdayoff?: string;
  day_off_option?: number | string;
  dayoff_option?: number | string;
}

/** The leave date of a range item, tolerating both field spellings. */
export const rangeDate = (r: DayOffRangeTime): string => r.start_day_off || r.startdayoff || '';

export interface MyDayOff {
  day_off_id: number;
  employee_name: string;
  position_name?: string;
  day_off_range_time: DayOffRangeTime[];
  day_off_reason: string;
  day_off_note: string;
  day_off_status: string;
  /** Slug, e.g. "annual_leave" / "unpaid_leave". */
  day_off_type: string;
  number_of_day_off: number;
  day_off_leader_confirmed?: boolean;
  day_off_bod_confirmed?: boolean;
  day_off_feedback_lead?: string;
  day_off_feedback_bod?: string;
  department_name: string;
  day_off_created_at: string;
}

/* ── My business trips ─────────────────────────────────────────────────── */
export interface MyBusiness {
  business_id: number;
  business_type: string;
  business_place: string;
  employee_name: string;
  business_startdate: string;
  business_enddate: string;
  business_content: string;
  business_status: string;
  business_created_at: string;
}

/* ── My workdays ───────────────────────────────────────────────────────── */
export interface RelatedDayOff {
  day_off_status?: string;
}
export interface RelatedBusiness {
  business_status?: string;
}

export interface MyWorkday {
  /** null on an "empty" calendar day (no attendance record). */
  work_day_id: number | null;
  work_day_date: string;
  work_day_time: number | null;
  /** Counted work fraction: 1 = full day, 0.5 = half, 0 = none. */
  work_n_day: number | null;
  work_day_in: string | null;
  work_day_out: string | null;
  work_day_type: string | null;
  work_day_option: number | null;
  work_day_note: string | null;
  employee_name: string;
  related_day_offs?: RelatedDayOff[];
  related_businesses?: RelatedBusiness[];
}

function toQuery(params: ListParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  return q.toString();
}

function paged<T>(url: string) {
  return dataCentralApi.get<ResponseArray<T>>(url).then((res) => {
    const data = res.data as ResponseArray<T> & { result?: T[] };
    return { results: data.results ?? data.result ?? [], pagination: data.pagination };
  });
}

export function useMyDayOffs(params: ListParams = {}) {
  return useQuery({
    queryKey: ['account', 'day-offs', params],
    queryFn: () => paged<MyDayOff>(`/employees/day_offs?${toQuery({ page: 1, page_size: 20, ...params })}`),
  });
}

/** Day-off summary counts (`/employees/day_offs/statistical`). */
export interface DayOffSummary {
  pending_total: number;
  rejected_total: number;
}

export function useDayOffSummary() {
  return useQuery({
    queryKey: ['account', 'day-offs', 'summary'],
    queryFn: async (): Promise<DayOffSummary> => {
      const res = await dataCentralApi.get<ResponseObject<DayOffSummary>>('/employees/day_offs/statistical');
      return res.data.result ?? { pending_total: 0, rejected_total: 0 };
    },
  });
}

export function useMyBusinesses(params: ListParams = {}) {
  return useQuery({
    queryKey: ['account', 'businesses', params],
    queryFn: () => paged<MyBusiness>(`/businesses?${toQuery({ page: 1, page_size: 20, ...params })}`),
  });
}

/** Business-trip summary counts (`/businesses/statistical`). */
export interface BusinessSummary {
  approved_total: number;
  pending_total: number;
  rejected_total: number;
}

export function useBusinessSummary() {
  return useQuery({
    queryKey: ['account', 'businesses', 'summary'],
    queryFn: async (): Promise<BusinessSummary> => {
      const res = await dataCentralApi.get<ResponseObject<BusinessSummary>>('/businesses/statistical');
      return res.data.result ?? { approved_total: 0, pending_total: 0, rejected_total: 0 };
    },
  });
}

/**
 * Attendance for one month. The backend expects the year/month as a base64-
 * encoded JSON `filters` param (not raw query keys) — matching the legacy
 * `getWorkDayByEmployeeID`. `page_size: 40` covers a full month.
 */
export function useMyWorkdays(year: number, month: number) {
  const filters = btoa(JSON.stringify({ year, month }));
  return useQuery({
    queryKey: ['account', 'workdays', year, month],
    queryFn: () => paged<MyWorkday>(`/workdays?page=1&page_size=40&filters=${encodeURIComponent(filters)}`),
  });
}

/** Monthly workday totals (`/workdays/total`) — raw `year`/`month` params here. */
export interface WorkdayTotal {
  total_work_days_in_month?: number;
  total_work_time?: number;
  [key: string]: number | undefined;
}

export function useMyWorkdayTotal(year: number, month: number, employeeId?: number) {
  return useQuery({
    queryKey: ['account', 'workday-total', year, month, employeeId],
    queryFn: async (): Promise<WorkdayTotal> => {
      const res = await dataCentralApi.get<ResponseObject<WorkdayTotal>>(
        `/workdays/total?${toQuery({ year, month, employee_id: employeeId })}`,
      );
      return res.data.result ?? {};
    },
  });
}
